import * as Yup from "yup";
import React, { useState } from "react";
import { Grid } from "@material-ui/core";

import BaseInput from "../../../../../components/elements/BaseInput";
import Button from "../../../../../components/elements/Button";
import PrimaryButton from "../../../../../components/redesign/elements/Button/PrimaryButton";
import SecondaryButton from "../../../../../components/redesign/elements/Button/SecondaryButton";
import api from "../../../../../api";
import { errorToaster } from "../../../../../helpers/toasters";
import { useCampaignDashboardStyles } from "../../CampaignDashboardStylesContext";

const useNewDesign = process.env.REACT_APP_FEATURE_NEW_DESIGN === "true";

type LandingPageEditModeProps = {
  landingPage: string;
  changeMode: () => void;
  adGroupId: string | undefined;
  fetchAndSetCreatives: () => Promise<void>;
};

const validationSchema = Yup.object({
  customUrl: Yup.string()
    .required("Required field.")
    .transform((value) => value.trim())
    .matches(
      /^(?:ht{2}ps?:\/{2})?(?:[\dA-Za-z-]+\.)+[A-Za-z]{2,}(?::\d+)?(?:\/[\w/-]*)?(?:\.html)?(\?[^\s#]*)?(#\S*)?$/,
      "Invalid URL format",
    ),
});

const updateLandingPage = async (adGroupId: string, landingPage: string) => {
  try {
    return await api.updateCreativesLandingPage(adGroupId, landingPage);
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const LandingPageEditMode = ({
  landingPage,
  changeMode,
  adGroupId,
  fetchAndSetCreatives,
}: LandingPageEditModeProps) => {
  const classes = useCampaignDashboardStyles();
  const [inputValue, setInputValue] = useState<string>(landingPage);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setError(null);
  };

  const handleSaveClick = async () => {
    try {
      await validationSchema.validate({ customUrl: inputValue });
    } catch (validationError) {
      const validationErr = validationError as any;
      setIsLoading(false);
      setError(validationErr.message);
      return;
    }

    try {
      if (adGroupId !== undefined) {
        setIsLoading(true);
        await updateLandingPage(adGroupId, inputValue);
        await fetchAndSetCreatives();
        setIsLoading(false);
        changeMode();
      }
    } catch (uploadingError) {
      const uploadingErr = uploadingError as any;
      setIsLoading(false);
      errorToaster(`There was an error: ${uploadingErr.message}`);
    }
  };

  return (
    <Grid className={classes.landingPageContainer}>
      <div className={classes.landingPageInputWrap}>
        <BaseInput
          value={inputValue}
          onChange={handleInputChange}
          variant="outlined"
          label="Landing Page"
          required
          error={!!error}
        />
      </div>
      {error && <div className={classes.errorText}>{error}</div>}
      {useNewDesign ? (
        <>
          <PrimaryButton onClick={handleSaveClick} disabled={isLoading}>
            Save
          </PrimaryButton>
          <SecondaryButton onClick={changeMode} disabled={isLoading}>
            Cancel
          </SecondaryButton>
        </>
      ) : (
        <>
          <Button onClick={handleSaveClick} isLoading={isLoading}>
            Save
          </Button>
          <Button variant="outlined" onClick={changeMode} disabled={isLoading}>
            Cancel
          </Button>
        </>
      )}
    </Grid>
  );
};

export default LandingPageEditMode;
